import { rowPropertyFields } from '@datasets/handsontable/read-grid/readGridToolbar/components/toggleColumns/config'
import { transformKeysToSnakeCase } from '@global/utils/conversions'
import { CreativeConfiguration } from '@templates/types'
import { convertUtcDateStringToLocal } from '@utils/convertUtcDateStringToLocal'
import { Classification } from './types'
import { Field } from '@datasets/handsontable/read-grid/readGridToolbar/components/toggleColumns/types'

const DATE_FORMAT = 'YYYY-MM-DD hh:mm A'

interface ITransformDataset {
  id: string
  rows: any[]
  columns: any[]
  addRowPropertyFields?: boolean
  allColumns?: any[]
  showHidden: boolean
  defaultFilteredColumns?: any[]
  creativeConfiguration?: CreativeConfiguration[]
  classifications?: Classification[]
}

export interface IDatasetRow {
  [x: string]: any
  data: Record<
    string | number,
    { id?: any; value: any; comment?: { commenter: string; message: string } }
  >
  classification?: {
    summary?: {
      categories?: {
        categoryId: string
        tags: Record<string, string[]>
      }[]
    }
  } | null
}

export type IDatasetById = Record<
  string,
  {
    rows: IDatasetRow[]
    columns: Field[]
    columnsByFieldId: any
    allColumns: any[]
    filteredColumns: Field[] | null
  }
>

interface transformClassificationsIntoColumns {
  creativeConfiguration?: CreativeConfiguration[]
  classifications?: Classification[]
  toSnakeCase?: boolean
}
/**
 * Transforms a classification object into an array of columns.
 * @param {CreativeConfiguration[]} creativeConfiguration - The creativeConfiguration to transform into columns.
 * @param {Classification[]} classifications - The classifications to use for the transformation.
 * @param {boolean} toSnakeCase - Flag to determine if the keys should be transformed to snake case.
 * @returns {Object[]} An array of columns.
 * @example
 *
 * const creativeConfiguration = [
 *  {
 *    categoryId: 1,
 *    threhshold: 0.5,
 *  },
 *  {
 *   categoryId: 2,
 *   threhshold: 0.5,
 *  }
 * ]
 *
 * const classifications = [
 *  {
 *    id: 1,
 *    categoryKey: 'objects',
 *    categoryText: 'Objects',
 *    createdAt: 2024-01-01T00:00:00Z,
 *    updatedAt: 2024-01-01T00:00:00Z,
 *  },
 *  {
 *    id: 2,
 *    categoryKey: 'emotions',
 *    categoryText: 'Emotions',
 *    createdAt: 2024-01-01T00:00:00Z,
 *    updatedAt: 2024-01-01T00:00:00Z,
 *  },
 * ]
 *
 *
 * // returns
 * [
 *  {
 *   defaultHidden: false,
 *   description: null,
 *   fieldId: 1,
 *   governanceType: "text",
 *   isClassificationColumn: true,
 *   listDelimiter: null,
 *   name: "Objects",
 *   visible: true,
 *  },
 *  {
 *   defaultHidden: false,
 *   description: null,
 *   fieldId: 2,
 *   governanceType: "text",
 *   isClassificationColumn: true,
 *   listDelimiter: null,
 *   name: Emotions,
 *   visible: true,
 *  }
 * ]
 *
 */
export const transformClassificationsIntoColumns = ({
  creativeConfiguration,
  classifications,
  toSnakeCase = false,
}: transformClassificationsIntoColumns) => {
  if (!creativeConfiguration?.length || !classifications?.length) return []

  return creativeConfiguration.map(({ categoryId }) => {
    const classification = classifications.find(({ id }) => id === categoryId)

    const column = {
      defaultHidden: false,
      description: null,
      fieldId: categoryId,
      governanceType: 'creative',
      isClassificationColumn: true,
      listDelimiter: null,
      name: classification?.categoryText ?? '',
      visible: true,
      visibleOnForm: true,
      field: {
        segmentType: 'text',
        name: classification?.categoryText ?? '',
        governanceType: 'creative',
      },
    }

    return toSnakeCase ? transformKeysToSnakeCase(column) : column
  })
}

/**
 * Transforms a dataset into a format indexed by its ID.
 * For use in setting the React Query cache for datasetDataById setter `addDatasetDataById`
 *
 * @param {Object} param0
 * @param {string} param0.id - Unique identifier for the dataset.
 * @param {any[]} param0.rows - Array of rows in the dataset.
 * @param {any[]} param0.columns - Array of columns in the dataset.
 * @param {boolean} param0.addRowPropertyFields - Flag to determine if row property fields should be added.
 * @param {any[]} param0.allColumns - Array of all columns, regardless of visibility or other filters.
 * @param {boolean} param0.showHidden - Flag to determine if hidden columns should be shown.
 * @param {any[]} param0.defaultFilteredColumns - Array of columns that should be shown by default when filtering.
 * @param {Object} param0.creativeConfiguration - AI Creative Classifications object.
 * @param {Object[]} param0.classifications - Array of classifications.
 *
 * @returns {IDatasetById} Transformed dataset indexed by ID.
 */
export function transformDatasetToById({
  id,
  rows,
  columns,
  addRowPropertyFields,
  allColumns,
  showHidden,
  defaultFilteredColumns,
  creativeConfiguration,
  classifications,
}: ITransformDataset): IDatasetById {
  const columnsToSort = allColumns ?? columns
  const withRowProperties = [...new Set([...rowPropertyFields, ...columnsToSort])]

  const sortedColumns = (addRowPropertyFields ? withRowProperties : columnsToSort).sort(
    (a, b) => a.precedence - b.precedence,
  )

  const alreadyAddedClassificationColumns = sortedColumns.some(
    column => column.isClassificationColumn,
  )

  const aiClassificationColumns = alreadyAddedClassificationColumns
    ? []
    : transformClassificationsIntoColumns({ creativeConfiguration, classifications })

  const visibleColumns = [...sortedColumns, ...aiClassificationColumns].filter(
    col => col.visible || showHidden,
  )
  const columnsByFieldId = columns.reduce(
    (memo, col, index) => ({
      ...memo,
      [col.fieldId]: index,
    }),
    {},
  )

  const rowsWithFormattedProperties = addRowPropertyFields
    ? rows.map(row => {
        const updatedRow = structuredClone(row)

        if (updatedRow?.properties?.createdAt) {
          updatedRow.properties.createdAt = convertUtcDateStringToLocal(
            row.properties.createdAt,
            DATE_FORMAT,
          )
        }

        if (updatedRow?.properties?.updatedAt) {
          updatedRow.properties.updatedAt = convertUtcDateStringToLocal(
            row.properties.updatedAt,
            DATE_FORMAT,
          )
        }

        return updatedRow
      })
    : rows

  return {
    [id]: {
      rows: rowsWithFormattedProperties,
      columns: visibleColumns,
      columnsByFieldId,
      allColumns: [...sortedColumns, ...aiClassificationColumns],
      filteredColumns: defaultFilteredColumns || null,
    },
  }
}
